import React, { useCallback, useEffect, useRef } from 'react';
import '../../Styles/Chat.css';
import useDocumentTitle from '../../UseDocumentTitle';
import { ChatListView } from '../Partials/ChatListView';
import { SearchModal } from '../Search/SearchModal';
import FileDrop from '../Partials/FileDrop';
import { useAppData, useChatContext } from '../../contexts/AppDataContext';
import { ChatNotFound } from '../Layout/ChatNotFound';
import { useAttachmentContext } from '../../contexts/AttachmentContext';
import { ModelModal } from '../Layout/Model/ModelModal';
import { useInputContext } from '../../contexts/InputContext';
 
import { ChatHeader } from './components/ChatHeader';
import { ChatErrorBoundary } from './components/ChatErrorBoundary';
import { ChatContainer } from './components/ChatContainer';
import { useMessageSubmission, type SubmitEvent } from './hooks/useMessageSubmission';
import { useUrlMessageHandler } from './hooks/useUrlMessageHandler';
import { useCleanupHandlers } from './hooks/useCleanupHandlers';
import useScrollHandler from '../../hooks/useScrollHandler';
import { AskUserProvider } from '../../contexts/AskUserContext';

interface ChatsProps {
  chatsListView?: boolean;
}

const Chats: React.FC<ChatsProps> = React.memo(({ chatsListView = false }) => {
  const { 
    filteredMessages,
    currentChatId,
    isGeneratingResponse,
    isSubmitting,
    memoizedChatTitle
  } = useChatContext();
  const { isGoogleDriveModalOpen } = useAttachmentContext();
  const { chatNotExist } = useAppData();

  // Set document title to chat title if available, otherwise use default
  // Filter out "new chat", empty strings, and when currentChatId is "new"
  const shouldUseTitle = memoizedChatTitle && 
    memoizedChatTitle.trim() && 
    memoizedChatTitle.toLowerCase() !== 'new chat' &&
    currentChatId !== 'new';
  
  useDocumentTitle(shouldUseTitle ? memoizedChatTitle : 'StudyBuddy');

  const { processFiles } = useAttachmentContext();
  
  const { 
    input, 
    setInput,
    resetHistory,
    focusInput,
    checkMobile,
    updateHandleSubmitInTextArea
  } = useInputContext();

  // Create a ref for scroll handler
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);

  // Create scroll handler for submission hook
  const { scrollToBottomGenerous } = useScrollHandler({
    messagesContainerRef: messagesContainerRef as React.RefObject<HTMLDivElement>,
    isGeneratingResponse,
    chatId: currentChatId || '',
    messages: filteredMessages
  });

  // Main submission logic hook
  const {
    handleSubmit,
    handleStopStreaming,
    regenerateMessage,
    answerQuestion,
    abortControllerRef
  } = useMessageSubmission({
    scrollToBottomGenerous,
    resetHistory,
    checkMobile,
    focusInput
  });

  const handleSubmitRef = useRef(handleSubmit);
  useEffect(() => {
    handleSubmitRef.current = handleSubmit;
  }, [handleSubmit]);

  // Stable identity for callers (e.g. StudyPanel) so they always hit latest handleSubmit.
  const triggerSubmit = useCallback((e?: SubmitEvent | React.FormEvent) => {
    if (e && typeof (e as SubmitEvent).preventDefault === 'function') {
      (e as SubmitEvent).preventDefault();
    }
    const eventObj = (e ?? { preventDefault: () => {} }) as SubmitEvent;
    void handleSubmitRef.current(eventObj);
  }, []);

  // URL message handler
  useUrlMessageHandler({
    isSubmitting,
    input,
    setInput,
    triggerSubmit
  });

  // Cleanup handlers
  useCleanupHandlers({
    isGeneratingResponse,
    filteredMessages,
    handleStopStreaming
  });

  // Update handlers in edit components
  useEffect(() => {
    updateHandleSubmitInTextArea(handleSubmit);
  }, [handleSubmit, updateHandleSubmitInTextArea]);

  const handleFilesDrop = useCallback((event: React.ChangeEvent<HTMLInputElement> | { target: { files: File[] } }) => {
    processFiles(event as React.ChangeEvent<HTMLInputElement>);
  }, [processFiles]);

  if (chatsListView) {
    return <ChatListView />;
  }

  return (
    <ChatErrorBoundary>
      <FileDrop onFilesDrop={handleFilesDrop}>
        <div className="chat-page">
          <ChatHeader />
          <ModelModal />
          <SearchModal />

          {chatNotExist && !isGoogleDriveModalOpen ? (
            <ChatNotFound />
          ) : (
            <AskUserProvider answerQuestion={answerQuestion}>
              <ChatContainer
                handleSubmit={handleSubmit}
                handleStopStreaming={handleStopStreaming}
                regenerateMessage={regenerateMessage}
                triggerSubmit={triggerSubmit}
                abortControllerRef={abortControllerRef}
                messagesContainerRef={messagesContainerRef as React.RefObject<HTMLDivElement>}
              />
            </AskUserProvider>
          )}
        </div>
      </FileDrop>
    </ChatErrorBoundary>
  );
});

Chats.displayName = 'Chats';

export default Chats;
